import localStorageEx from 'bundles/common/utils/localStorageEx';
import type {
  PersonalizedMessageList,
  PersonalizedMessageName,
} from 'bundles/degree-description/utils/personalizedMessageContent';

export const PERSONALIZED_MESSAGES_VIEWS_LOCALHOST = 'personalizedMessageViews';
export const CURRENT_PERSONALIZED_MESSAGES = 'currentPersonalizedMessages';
export const PERSONALIZED_MESSAGES_CLOSE_COUNT = 'personalizedMessageCloseCount';

export type CurrentPersonalizedMessagesWithSlug = {
  messages: PersonalizedMessageName[];
  degreeSlug: string;
};

/**
 * The interior type:
 * {
 *   enrolledStackableRelatedTo: 1,
 *   enrolledStackableOffersCredit: 2
 * }
 */
export type PersonalizedMessageNameCounts<Messages> = {
  [Property in keyof Messages]: number;
};

/**
 * Personalized Message view counts look like:
 * {
 *   imba: {
 *     enrolledStackableRelatedTo: 1,
 *     enrolledStackableOffersCredit: 2
 *   },
 *   omie: {
 *     enrolledStackableRelatedTo: 2,
 *     enrolledStackableOffersCredit: 3
 *   }
 * }
 */
export type PersonalizedMessageViews = {
  [slug: string]: PersonalizedMessageNameCounts<PersonalizedMessageList>;
};

export type PersonalizedMessageData = {
  currentPersonalizedMessages: CurrentPersonalizedMessagesWithSlug | undefined;
  personalizedViewCount: PersonalizedMessageViews;
};

/**
 * Get the currently active personalized messages
 * @returns CurrentPersonalizedMessagesWithSlug, an array of current messages along with the degree slug it's set on
 */
export const getCurrentPersonalizedMessages = (): CurrentPersonalizedMessagesWithSlug | undefined => {
  return localStorageEx.getItem(CURRENT_PERSONALIZED_MESSAGES, JSON.parse, undefined);
};

/**
 * Sets the currently active personalized messages on a degree program
 * @param degreeSlug A string of the degree slug, e.g. `imba`
 * @param messages An array of message names, as they're set in the `personalizedMessageContent.ts` util file
 */
export const setCurrentPersonalizedMessages = (degreeSlug: string, messages: PersonalizedMessageName[]) => {
  const messageWithSlug = {
    messages,
    degreeSlug,
  };
  localStorageEx.setItem(CURRENT_PERSONALIZED_MESSAGES, messageWithSlug, JSON.stringify);
};

/**
 * Gets the close count for all programs, as set in localStorage
 * @returns PersonalizedMessageViews, an object listing out the close count of all personalized messages on all programs
 */
export const getClosedPersonalizedMessageCount = (): PersonalizedMessageViews => {
  return localStorageEx.getItem(PERSONALIZED_MESSAGES_CLOSE_COUNT, JSON.parse, undefined);
};

/**
 * Increments the number of times a personalized message count has been closed by one, and adds it to the localStorage object
 * @param degreeSlug A string that represents the degree slug
 * @param messageName The name of the personalized message count to increment
 */
export const incrementClosedPersonalizedMessageCount = (degreeSlug: string, messageName: PersonalizedMessageName) => {
  const closeCount = getClosedPersonalizedMessageCount();

  if (closeCount) {
    const currentCloseCount = closeCount?.[degreeSlug]?.[messageName] ?? 0;

    const incrementedMessageCountObj = {
      ...closeCount,
      [degreeSlug]: {
        ...closeCount[degreeSlug],
        [messageName]: currentCloseCount + 1,
      },
    };

    localStorageEx.setItem(PERSONALIZED_MESSAGES_CLOSE_COUNT, incrementedMessageCountObj, JSON.stringify);
  } else {
    // The LocalStorage object doesn't exist, so set this for the first time.
    localStorageEx.setItem(
      PERSONALIZED_MESSAGES_CLOSE_COUNT,
      {
        [degreeSlug]: {
          [messageName]: 1,
        },
      },
      JSON.stringify
    );
  }
};

/**
 * Gets the message view count for all programs, as set in localStorage
 * @returns PersonalizedMessageViews, an object listing out the view count of all personalized messages on all programs
 */
export const getMessageViewCount = (): PersonalizedMessageViews => {
  return localStorageEx.getItem(PERSONALIZED_MESSAGES_VIEWS_LOCALHOST, JSON.parse, undefined);
};

/**
 * Increments a personalized message count by one, and adds it to the localStorage object
 * @param degreeSlug A string that represents the degree slug
 * @param messageName The name of the personalized message count to increment
 */
export const incrementMessageViewCount = (degreeSlug: string, messageName: PersonalizedMessageName) => {
  const currentViews = getMessageViewCount();

  if (currentViews) {
    const currMessageCount = currentViews?.[degreeSlug]?.[messageName] ?? 0;

    const incrementedMessageCountObj = {
      ...currentViews,
      [degreeSlug]: {
        ...currentViews[degreeSlug],
        [messageName]: currMessageCount + 1,
      },
    };

    localStorageEx.setItem(PERSONALIZED_MESSAGES_VIEWS_LOCALHOST, incrementedMessageCountObj, JSON.stringify);
  } else {
    // The LocalStorage object doesn't exist, so set this for the first time.
    localStorageEx.setItem(
      PERSONALIZED_MESSAGES_VIEWS_LOCALHOST,
      {
        [degreeSlug]: {
          [messageName]: 1,
        },
      },
      JSON.stringify
    );
  }
};
